import { useQuery } from '@tanstack/react-query';
import { TeamsApi } from '@up2stay/up2promo-api-client';

import { fetchAllProfileTemplatesPages } from '@/core/lib/profile-templates-fetch-all';
import { shouldRetryQuery } from '@/core/lib/list-pagination';
import { useAuthStore } from '@/core/store/auth.store';

/**
 * Resolves the profile template assigned to the user's team in a workspace.
 */
export function useTeamAssignedProfileTemplate(
  workspaceId: string | null | undefined,
) {
  const userId = useAuthStore((s) => s.user?.id);

  const query = useQuery({
    queryKey: ['team-assigned-profile-template', workspaceId, userId],
    queryFn: async () => {
      if (!workspaceId || !userId) return null;

      const teamsRes = await TeamsApi.findAll({
        path: { workspaceId },
        query: { page: 1, limit: 5, onlyMine: true },
      });
      const myTeamId = teamsRes.data?.data?.[0]?.id;
      if (!myTeamId) return null;

      const templates = await fetchAllProfileTemplatesPages(workspaceId, {
        onlyMine: true,
      });
      return templates.find((t) => t.teamIds?.includes(myTeamId)) ?? null;
    },
    enabled: Boolean(workspaceId && userId),
    staleTime: 60 * 1000,
    retry: shouldRetryQuery,
  });

  return {
    teamTemplate: query.data ?? null,
    teamTemplateId: query.data?.id ?? null,
    isLoading: query.isLoading,
  };
}
